import React, { createContext, useContext, useState } from 'react';

const NotificationContext = createContext();

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);

  const addNotification = (notification) => {
    const id = Date.now().toString();
    const newNotification = {
      id,
      type: 'info',
      duration: 5000,
      ...notification
    };

    setNotifications(prev => [...prev, newNotification]);

    // Auto remove after duration
    if (newNotification.duration !== 0) {
      setTimeout(() => {
        removeNotification(id);
      }, newNotification.duration);
    }

    return id;
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  };

  const clearAll = () => {
    setNotifications([]);
  };

  // Helper methods for different notification types
  const notify = {
    success: (message, options = {}) => {
      return addNotification({ type: 'success', message, ...options });
    },
    
    error: (message, options = {}) => {
      return addNotification({ type: 'error', message, ...options });
    },
    
    warning: (message, options = {}) => {
      return addNotification({ type: 'warning', message, ...options });
    },
    
    info: (message, options = {}) => {
      return addNotification({ type: 'info', message, ...options });
    }
  };

  const value = {
    notifications,
    addNotification,
    removeNotification,
    clearAll,
    notify
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <NotificationToast />
    </NotificationContext.Provider>
  );
};

const NotificationToast = () => {
  const { notifications, removeNotification } = useNotification();

  if (notifications.length === 0) return null;

  const getBackgroundColor = (type) => {
    switch (type) {
      case 'success': return 'bg-green-500';
      case 'error': return 'bg-red-500';
      case 'warning': return 'bg-yellow-500';
      case 'info': return 'bg-blue-500';
      default: return 'bg-gray-500';
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'success': return '✅';
      case 'error': return '❌';
      case 'warning': return '⚠️';
      case 'info': return 'ℹ️';
      default: return '💡';
    }
  };

  return (
    <div className="fixed top-4 right-4 z-50 space-y-2 max-w-sm">
      {notifications.map(notification => (
        <div
          key={notification.id}
          className={`${getBackgroundColor(notification.type)} text-white p-4 rounded-lg shadow-lg transform transition-all duration-300`}
        >
          <div className="flex items-start space-x-3">
            <span className="text-lg flex-shrink-0">
              {getIcon(notification.type)}
            </span>
            <div className="flex-1">
              <p className="text-sm font-medium">{notification.message}</p>
              {notification.description && (
                <p className="text-xs opacity-90 mt-1">{notification.description}</p>
              )}
            </div>
            <button
              onClick={() => removeNotification(notification.id)}
              className="flex-shrink-0 text-white hover:text-gray-200 transition-colors"
            >
              ✕
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default NotificationToast;